import { map } from 'lodash-es';
import * as C from '../../constants';
import SkiPassService from '../../../../services/ski/ski-pass-service';
import SkiService from '../../../../services/ski/ski-service';
import exceptionValidator from '../../../../utils/exceptionValidator';
import periodPriceHelper from '../skipass/periodPriceHelper';

export default {

  /**
   * @param commit
   */
  resetState({ commit }) {
    commit(C.SKI_PASS_RESET_STATE);
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  seasonsLoad({ commit }) {
    commit(C.SKI_PASS_SEASONS_LOAD_PENDING);
    return SkiPassService.getSeasonsWithRelations()
      .then(res => {
        commit(C.SKI_PASS_SEASONS_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_PASS_SEASONS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<void>|Promise<T>}
   */
  seasonByIdLoad({ commit, state }, seasonId) {
    commit(C.SKI_PASS_SEASON_LOAD_PENDING);
    if (seasonId === undefined) {
      const newSeasonItem = Object.assign({}, state.season.newSeasonItem);
      commit(C.SKI_PASS_SEASON_LOAD_SUCCESS, newSeasonItem);
      return Promise.resolve();
    }
    return SkiPassService.getSeasonById(seasonId)
      .then(res => {
        commit(C.SKI_PASS_SEASON_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_PASS_SEASON_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param seasonId
   * @returns {Promise<T>}
   */
  seasonWithRelationsBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_PASS_SEASON_LOAD_PENDING);
    return SkiPassService.getSeasonWithRelationsBySeasonId(seasonId)
      .then(res => {
        commit(C.SKI_PASS_SEASON_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_PASS_SEASON_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   */
  setSeason({ commit }, payload) {
    commit(C.SKI_PASS_SEASON_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  seasonCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_SEASON_CREATE_PENDING);
    return SkiPassService.createSeason(payload)
      .then(response => {
        const skiPassSeasonId = response.data.id;
        const newSeasonItem = Object.assign({}, payload);
        newSeasonItem.id = skiPassSeasonId;
        commit(C.SKI_PASS_SEASON_CREATE_SUCCESS, newSeasonItem);
        dispatch('seasonByIdLoad', newSeasonItem.id);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_SEASON_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  seasonSave({ commit }, payload) {
    commit(C.SKI_PASS_SEASON_SAVE_PENDING);
    return SkiPassService.saveSeason(payload)
      .then(() => {
        commit(C.SKI_PASS_SEASON_SAVE_SUCCESS, payload);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_SEASON_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  salesTypesLoad({ commit }) {
    commit(C.SKI_PASS_SALES_TYPES_LOAD_PENDING);
    return SkiPassService.getSalesTypes()
      .then(res => {
        commit(C.SKI_PASS_SALES_TYPES_LOAD_SUCCESS, res.data);
      })
      .catch(() => {
        commit(C.SKI_PASS_SALES_TYPES_LOAD_FAILURE);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  personCategoriesByIdLoad({ commit }, seasonId) {
    commit(C.SKI_PASS_PERSON_CATEGORIES_LOAD_PENDING);
    return SkiPassService.getPersonCategoriesById(seasonId)
      .then(res => {
        commit(C.SKI_PASS_PERSON_CATEGORIES_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_PASS_PERSON_CATEGORIES_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_PASS_PERSON_CATEGORIES_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  discountsBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_PASS_DISCOUNTS_LOAD_PENDING);
    return SkiPassService.getDiscountsBySeasonId(seasonId)
      .then(res => {
        const discounts = map(res.data, discount => ({
          id: discount.id,
          skiPassSeasonId: discount.skiPassSeasonId,
          bookFrom: discount.bookFrom,
          bookTo: discount.bookTo,
          arrivalFrom: discount.arrivalFrom,
          arrivalTo: discount.arrivalTo,
          minPax: discount.minPax,
          maxPax: discount.maxPax,
          percentage: discount.percentage,
          personCategories: discount.personCategories,
        }));
        commit(C.SKI_PASS_DISCOUNTS_LOAD_SUCCESS, discounts);
      })
      .catch(error => {
        commit(C.SKI_PASS_DISCOUNTS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  areasBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_PASS_AREAS_LOAD_PENDING);
    return SkiPassService.getAreasBySeasonId(seasonId)
      .then(res => {
        commit(C.SKI_PASS_AREAS_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_PASS_AREAS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonId
   * @param newItem
   * @returns {Promise<T>}
   */
  areaOnSeasonSave({ commit, dispatch }, { seasonId, newItem }) {
    commit(C.SKI_PASS_AREAS_SAVE_PENDING);
    return SkiPassService.saveAreaOnSeason(seasonId, newItem.id)
      .then(() => {
        commit(C.SKI_PASS_AREAS_SAVE_SUCCESS);
        dispatch('areasBySeasonIdLoad', seasonId);
      })
      .catch(() => {
        commit(C.SKI_PASS_AREAS_SAVE_FAILURE);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param seasonId
   * @param areaId
   * @returns {Promise<T>}
   */
  areaOnSeasonDelete({ commit, dispatch }, { seasonId, areaId }) {
    commit(C.SKI_PASS_AREAS_DELETE_PENDING);
    return SkiPassService.deleteAreaOnSeason(seasonId, areaId)
      .then(() => {
        commit(C.SKI_PASS_AREAS_DELETE_SUCCESS);
        dispatch('areasBySeasonIdLoad', seasonId);
      })
      .catch(() => {
        commit(C.SKI_PASS_AREAS_DELETE_FAILURE);
      });
  },

  /**
   * @param commit
   * @returns {Promise<T>}
   */
  suppliersLoad({ commit }) {
    commit(C.SKI_SUPPLIERS_LOAD_PENDING);
    return SkiService.getActiveSkiSuppliers()
      .then(res => {
        commit(C.SKI_SUPPLIERS_LOAD_SUCCESS, res.data);
      })
      .catch(error => {
        commit(C.SKI_SUPPLIERS_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   */
  personCategoryAddNew({ commit }) {
    commit(C.SKI_PASS_PERSON_CATEGORY_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setPersonCategory({ commit }, payload) {
    commit(C.SKI_PASS_PERSON_CATEGORIES_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  personCategoryCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PERSON_CATEGORY_CREATE_PENDING);
    return SkiPassService.createPersonCategory(payload)
      .then(() => {
        commit(C.SKI_PASS_PERSON_CATEGORY_CREATE_SUCCESS);
        dispatch('personCategoriesByIdLoad', payload.skiPassSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_PERSON_CATEGORY_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  personCategorySave({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PERSON_CATEGORY_SAVE_PENDING);
    return SkiPassService.savePersonCategory(payload)
      .then(() => {
        commit(C.SKI_PASS_PERSON_CATEGORY_SAVE_SUCCESS, payload);
        dispatch('personCategoriesByIdLoad', payload.skiPassSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_PERSON_CATEGORY_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param personCategoryId
   * @returns {Promise<void>|Promise<T>}
   */
  personCategoryRemove({ commit }, personCategoryId) {
    if (!personCategoryId) {
      commit(C.SKI_PASS_PERSON_CATEGORY_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_PASS_PERSON_CATEGORY_REMOVE_PENDING);
    return SkiPassService.deletePersonCategory(personCategoryId)
      .then(() => {
        commit(C.SKI_PASS_PERSON_CATEGORY_REMOVE_SUCCESS, personCategoryId);
      })
      .catch(error => {
        commit(C.SKI_PASS_PERSON_CATEGORY_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   */
  discountAddNew({ commit }) {
    commit(C.SKI_PASS_DISCOUNT_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setDiscount({ commit }, payload) {
    commit(C.SKI_PASS_DISCOUNT_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  discountCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_DISCOUNT_CREATE_PENDING);
    return SkiPassService.createDiscount(payload)
      .then(() => {
        commit(C.SKI_PASS_DISCOUNT_CREATE_SUCCESS);
        dispatch('discountsBySeasonIdLoad', payload.skiPassSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_DISCOUNT_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  discountSave({ commit }, payload) {
    commit(C.SKI_PASS_DISCOUNT_SAVE_PENDING);
    return SkiPassService.saveDiscount(payload)
      .then(() => {
        commit(C.SKI_PASS_DISCOUNT_SAVE_SUCCESS, payload);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_DISCOUNT_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param discountId
   * @returns {Promise<void>|Promise<T>}
   */
  discountRemove({ commit }, discountId) {
    if (!discountId) {
      commit(C.SKI_PASS_DISCOUNT_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_PASS_DISCOUNT_REMOVE_PENDING);
    return SkiPassService.deleteDiscount(discountId)
      .then(() => {
        commit(C.SKI_PASS_DISCOUNT_REMOVE_SUCCESS, discountId);
      })
      .catch(error => {
        commit(C.SKI_PASS_DISCOUNT_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param discountId
   * @param newItem
   * @returns {Promise<T>}
   */
  personCategoryOnDiscountSave({ commit, state }, { discountId, newItem }) {
    commit(C.SKI_PASS_DISCOUNT_PERSON_CATEGORY_SAVE_PENDING);
    return SkiPassService.savePersonCategoryOnDiscount(discountId, newItem.id)
      .then(() => {
        const items = [ ...state.season.discounts.items ];
        items.splice(0, 0, newItem);
        commit(C.SKI_PASS_DISCOUNT_PERSON_CATEGORY_SAVE_SUCCESS);
      })
      .catch(error => {
        commit(C.SKI_PASS_DISCOUNT_PERSON_CATEGORY_SAVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param discountId
   * @param personCategoryId
   * @returns {Promise<T>}
   */
  personCategoryOnDiscountDelete({ commit, state }, { discountId, personCategoryId }) {
    commit(C.SKI_PASS_DISCOUNT_PERSON_CATEGORY_DELETE_PENDING);
    return SkiPassService.deletePersonCategoryOnDiscount(discountId, personCategoryId)
      .then(() => {
        const index = Object.values(state.season.discounts.items).findIndex(
          item => item.id === discountId,
        );
        const items = [ ...state.season.discounts.items ];
        items.splice(index, 1, items);
        commit(C.SKI_PASS_DISCOUNT_PERSON_CATEGORY_DELETE_SUCCESS);
      })
      .catch(error => {
        commit(C.SKI_PASS_DISCOUNT_PERSON_CATEGORY_DELETE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param seasonId
   * @returns {Promise<T>}
   */
  periodsWithPricesBySeasonIdLoad({ commit }, seasonId) {
    commit(C.SKI_PASS_PERIODS_LOAD_PENDING);
    return SkiPassService.getPeriodsWithPricesBySeasonId(seasonId)
      .then(res => {
        const periods = map(res.data, period => ({
          id: period.id,
          skiPassSeasonId: period.skiPassSeasonId,
          arrivalFrom: period.arrivalFrom,
          arrivalTo: period.arrivalTo,
          prices: period.prices,
        }));
        commit(C.SKI_PASS_PERIODS_LOAD_SUCCESS, periods);
      })
      .catch(error => {
        if (exceptionValidator.isNotFoundException(error.response.data.identifier)) {
          commit(C.SKI_PASS_PERIODS_LOAD_SUCCESS, {});
        } else {
          commit(C.SKI_PASS_PERIODS_LOAD_FAILURE, error.response.data);
        }
      });
  },

  /**
   * @param commit
   */
  periodAddNew({ commit }) {
    commit(C.SKI_PASS_PERIOD_ADD);
  },

  /**
   * @param commit
   * @param payload
   */
  setPeriod({ commit }, payload) {
    commit(C.SKI_PASS_PERIOD_SET, payload);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  periodCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PERIOD_CREATE_PENDING);
    return SkiPassService.createPeriod(payload)
      .then(() => {
        commit(C.SKI_PASS_PERIOD_CREATE_SUCCESS);
        dispatch('periodsWithPricesBySeasonIdLoad', payload.skiPassSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_PERIOD_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<T>}
   */
  periodSave({ commit }, payload) {
    commit(C.SKI_PASS_PERIOD_SAVE_PENDING);
    return SkiPassService.savePeriod(payload)
      .then(() => {
        commit(C.SKI_PASS_PERIOD_SAVE_SUCCESS, payload);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_PERIOD_SAVE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param periodId
   * @returns {Promise<void>|Promise<T>}
   */
  periodRemove({ commit }, periodId) {
    if (!periodId) {
      commit(C.SKI_PASS_PERIOD_REMOVE_SUCCESS, undefined);
      return Promise.resolve();
    }
    commit(C.SKI_PASS_PERIOD_REMOVE_PENDING);
    return SkiPassService.deletePeriod(periodId)
      .then(() => {
        commit(C.SKI_PASS_PERIOD_REMOVE_SUCCESS, periodId);
      })
      .catch(error => {
        commit(C.SKI_PASS_PERIOD_REMOVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  periodToAnotherPeriodCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PERIOD_CREATE_PENDING);
    const currentPeriod = payload;
    const newPeriodItem = Object.assign({}, {
      skiPassSeasonId: currentPeriod.skiPassSeasonId,
      arrivalFrom: currentPeriod.arrivalFrom,
      arrivalTo: currentPeriod.arrivalTo,
    });
    return SkiPassService.createPeriodToAnotherPeriod(
      currentPeriod.id,
      newPeriodItem,
    )
      .then(() => {
        commit(C.SKI_PASS_PERIOD_CREATE_SUCCESS, payload);
        dispatch('periodsWithPricesBySeasonIdLoad', payload.skiPassSeasonId);
      })
      .catch(error => {
        const errorMessages = exceptionValidator.parseErrorException(error, true);
        commit(C.SKI_PASS_PERIOD_CREATE_FAILURE, errorMessages);
      });
  },

  /**
   * @param commit
   * @param periodId
   * @returns {Promise<T>}
   */
  pricesByPeriodIdLoad({ commit }, periodId) {
    commit(C.SKI_PASS_PERIOD_PRICE_LOAD_PENDING);
    return SkiPassService.getPeriodPricesByPeriodId(periodId)
      .then(res => {
        const prices = map(res.data, price => ({
          id: price.id,
          skiPassPersonCategoryId: price.skiPassPersonCategoryId,
          days: price.days,
          price: price.price,
          commissionRatio: price.commissionRatio,
          contributionRatio: price.contributionRatio,
          requireKeyCard: price.requireKeyCard,
        }));
        commit(C.SKI_PASS_PERIOD_PRICE_LOAD_SUCCESS, { periodId, prices });
      })
      .catch(error => {
        commit(C.SKI_PASS_PERIOD_PRICE_LOAD_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param state
   * @param payload
   */
  setPeriodPrice({ commit, state }, payload) {
    const periodId = payload.periodId;
    const priceItem = {
      ...Object.assign({}, payload.priceItem),
      ...payload.params,
    };
    const items = Object.values(state.season.pricePeriods.items).map(period => {
      const pricesArray = [
        ...Object.values(period.prices)
          .map(existingPrice => (
            periodPriceHelper.takeNewOrExistingPrice(periodId, period.id, existingPrice, priceItem)
          )),
      ];
      if (periodPriceHelper.shouldAddPriceItemToPrices(
        periodId,
        period.id,
        pricesArray,
        priceItem,
      )) {
        pricesArray.push(priceItem);
      }
      const prices = {};
      pricesArray.forEach(price => {
        prices[price.id] = Object.assign({}, price);
      });
      return {
        ...period,
        prices,
      };
    });

    commit(C.SKI_PASS_PERIOD_PRICE_SET, items);
  },

  /**
   * @param commit
   * @param state
   * @param payload
   */
  priceAddNew({ commit, state }, payload) {
    const periodId = payload.periodId;
    const selectedDay = payload.selectedDay;
    const newPriceItem = Object.assign({}, state.season.prices.newPriceItem);
    newPriceItem.days = selectedDay;
    const items = Object.values(state.season.pricePeriods.items).map(period => {
      const pricesArray = [
        ...Object.values(period.prices).map(existingPrice => (
          periodPriceHelper.samePeriod(period.id, periodId)
          && periodPriceHelper.isPriceMissingPersonCategory(existingPrice)
            ? newPriceItem
            : existingPrice
        )),
      ];
      if (periodPriceHelper.shouldAddNewUnknownPriceItemToPrices(
        period.id,
        periodId,
        pricesArray,
        newPriceItem,
      )) {
        pricesArray.push(newPriceItem);
      }
      const prices = {};
      pricesArray.forEach(price => {
        prices[price.id] = Object.assign({}, price);
      });

      return {
        ...period,
        prices,
      };
    });

    commit(C.SKI_PASS_PERIOD_PRICE_ADD, items);
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  priceCreate({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PERIOD_PRICE_CREATE_PENDING);
    return SkiPassService.createPeriodPrice(payload.periodId, payload.periodPrice)
      .then(() => {
        commit(C.SKI_PASS_PERIOD_PRICE_CREATE_SUCCESS);
        dispatch('pricesByPeriodIdLoad', payload.periodId);
      })
      .catch(error => {
        commit(C.SKI_PASS_PERIOD_PRICE_CREATE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param dispatch
   * @param payload
   * @returns {Promise<T>}
   */
  priceSave({ commit, dispatch }, payload) {
    commit(C.SKI_PASS_PERIOD_PRICE_SAVE_PENDING);
    return SkiPassService.savePeriodPrice(payload.periodId, payload.periodPrice)
      .then(() => {
        commit(C.SKI_PASS_PERIOD_PRICE_SAVE_SUCCESS);
        dispatch('pricesByPeriodIdLoad', payload.periodId);
      })
      .catch(error => {
        commit(C.SKI_PASS_PERIOD_PRICE_SAVE_FAILURE, error.response.data);
      });
  },

  /**
   * @param commit
   * @param payload
   * @returns {Promise<void>|Promise<T>}
   */
  priceRemove({ commit }, payload) {
    commit(C.SKI_PASS_PERIOD_PRICE_REMOVE_PENDING);
    if (!payload.priceId) {
      commit(C.SKI_PASS_PERIOD_PRICE_REMOVE_SUCCESS, payload);
      return Promise.resolve();
    }
    return SkiPassService.deletePeriodPrice(payload.periodId, payload.priceId)
      .then(() => {
        commit(C.SKI_PASS_PERIOD_PRICE_REMOVE_SUCCESS, payload);
      })
      .catch(error => {
        commit(C.SKI_PASS_PERIOD_PRICE_REMOVE_FAILURE, error.response.data);
      });
  },

  getSeasonPickupPlaces({ commit, state }) {
    commit(C.SKI_PASS_SEASON_PICK_UP_PLACES_PENDING);
    const seasonId = state.season.id;
    return SkiService.getPickupPlaces(seasonId).then(res => {
      commit(C.SKI_PASS_SEASON_PICK_UP_PLACES_SUCCESS, res.data);
    }).catch(() => {
      commit(C.SKI_PASS_SEASON_PICK_UP_PLACES_FAILURE);
    });
  },

  updateSeasonPickupPlaces({ state, dispatch }, ids) {
    const seasonId = state.season.id;
    return SkiService.updateSeasonPickupPlaces({ seasonId, ids }).then(() => {
      dispatch('snackbar/show', {
        identifier: 'success',
        message: 'Saved pickup places',
      }, { root: true });
    }).catch(() => {
      dispatch('snackbar/show', {
        identifier: 'error',
        message: 'Failed to save pickup places',
      }, { root: true });
    });
  },
};



// WEBPACK FOOTER //
// ./src/store/modules/ski/skipass/actions.js